/**
 * AskUserQuestion: the agent asks, a human answers, on the log.
 *
 * The tool contract is transcribed from Claude Code's own built-in
 * `AskUserQuestion` — schema, prose, and the steering messages it returns on
 * bad input — so a model that already knows the native tool behaves identically
 * here, and so a harness-backed session and a Mod-hosted one ask questions the
 * same way. The prose is load-bearing: it is what stops a model asking about
 * things it could have looked up.
 *
 * Two deliberate departures, both forced by Mod delivering the answer as a tool
 * RESULT rather than rewriting the recorded call the way Claude Code's harness
 * does:
 *
 * 1. The native input schema also carries `answers`, `annotations`, and
 *    `metadata`. Those are not model-authored — the harness writes them back.
 *    Advertising them would be accepted-but-ignored input that invites a model
 *    to invent its own answers, so they are omitted from the input and returned
 *    in the result instead (`ToolResult` reproduces their shape exactly).
 * 2. `multiSelect` is advertised as required-with-a-default exactly as Claude
 *    does, but validated leniently, so a model that omits it gets a
 *    single-select question rather than a validation error.
 *
 * What is Mod's own is the TRANSPORT: the question becomes a log entry, so the
 * surface that renders it need not be the peer that asked. See
 * `InteractionRequestedData`.
 */

/** Mirrored from Claude Code's own tool so a model hits the same walls here. */
export const MAX_QUESTIONS = 4
export const MAX_OPTIONS = 4
export const MAX_FREE_TEXT_LENGTH = 4096
/**
 * Per-option preview cap. Previews are freeform (mockups, diffs, snippets) and
 * every one rides the log entry, so the cap keeps a single question from
 * bloating the session document on its own.
 */
export const MAX_OPTION_PREVIEW_LENGTH = 4096

// ---------------------------------------------------------------------------
// Tool input — the shape the model sends.
// ---------------------------------------------------------------------------

export interface ToolOption {
  label: string
  description: string
  preview?: string
}

export interface ToolQuestion {
  question: string
  header: string
  options: ToolOption[]
  multiSelect?: boolean
}

export interface ToolInput {
  questions: ToolQuestion[]
}

// ---------------------------------------------------------------------------
// Interaction body — what rides the log and what a surface renders.
// ---------------------------------------------------------------------------

export interface InteractionOption {
  value: string
  label: string
  description?: string
  preview?: string
}

export interface InteractionQuestion {
  id: string
  prompt: string
  shortLabel: string
  multiSelect: boolean
  options: InteractionOption[]
  allowFreeText: boolean
}

export interface QuestionBody {
  questions: InteractionQuestion[]
}

// ---------------------------------------------------------------------------
// Interaction answer — what the surface writes back.
// ---------------------------------------------------------------------------

export interface InteractionAnswerValue {
  selected: string[]
  freeText?: string
}

export interface QuestionAnswer {
  answers: Record<string, InteractionAnswerValue>
}

// ---------------------------------------------------------------------------
// Tool result — what the model reads back.
// ---------------------------------------------------------------------------

export interface ToolResultQuestion {
  question: string
  header: string
  options: Array<{ label: string; description: string; preview?: string }>
  multiSelect: boolean
}

export interface ToolResultAnnotation {
  preview?: string
  notes?: string
}

/**
 * Claude Code's own output schema: the questions asked, answers keyed by
 * question text (multi-select comma-separated), an optional freeform
 * `response` for text typed instead of choosing, and optional per-question
 * annotations. Reproduced field-for-field so the model reads a familiar result.
 */
export interface ToolResult {
  questions: ToolResultQuestion[]
  answers: Record<string, string>
  response?: string
  annotations?: Record<string, ToolResultAnnotation>
}

// ---------------------------------------------------------------------------
// The advertised contract.
// ---------------------------------------------------------------------------

/**
 * Verbatim from Claude Code's `AskUserQuestion` description, in the markdown
 * preview variant (Mod renders previews as markdown in a monospace box, so the
 * HTML-fragment variant would describe the wrong format).
 *
 * One sentence is adapted rather than copied: Claude's says the UI "switches to
 * a side-by-side layout with a vertical option list on the left and preview on
 * the right". Mod reveals the preview beneath the selected option instead, and
 * describing a layout the model cannot produce would be a lie in a paragraph
 * whose entire job is telling it what the preview will look like.
 */
export const ASK_USER_QUESTION_DESCRIPTION = `Use this tool only when you are blocked on a decision that is genuinely the user's to make: one you cannot resolve from the request, the code, or sensible defaults.

Usage notes:
- Users will always be able to select "Other" to provide custom text input
- Use multiSelect: true to allow multiple answers to be selected for a question
- If you recommend a specific option, make that the first option in the list and add "(Recommended)" at the end of the label

Reserve this for decisions where the user's answer changes what you do next — not for choices with a conventional default or facts you can verify in the codebase yourself. In those cases pick the obvious option, mention it in your response, and proceed.

Preview feature:
Use the optional \`preview\` field on options when presenting concrete artifacts that users need to visually compare:
- ASCII mockups of UI layouts or components
- Code snippets showing different implementations
- Diagram variations
- Configuration examples

Preview content is rendered as markdown in a monospace box. Multi-line text with newlines is supported. The preview is revealed beneath an option once it is selected. Do not use previews for simple preference questions where labels and descriptions suffice. Note: previews are only supported for single-select questions (not multiSelect).

The question may be answered from a different device than the one you are running on, so it can take a while. Ask, then wait.
`

/**
 * Claude's steering text for a question with fewer than two options, verbatim.
 * Deliberately more than an error: it tells the model to stop asking and
 * proceed, rather than to retry with a padded option list.
 */
export const TOO_FEW_OPTIONS_MESSAGE =
  'This call included a question with fewer than 2 options, so it was rejected and the person never saw it. A question with a single option has no decision in it. Do not retry this call and do not invent a filler second option. Instead, state the one path you were going to offer as the approach you are taking, then continue with the task. If this call also contained questions with 2 to 4 options (each with distinct labels), you may re-ask those questions alone in a new call. Ask a question only when the person has at least two genuinely distinct choices.'

/** Claude's uniqueness-refinement message, verbatim. */
export const NOT_UNIQUE_MESSAGE =
  'Question texts must be unique, option labels must be unique within each question'

/** Claude's away-from-keyboard result, verbatim apart from the elapsed seconds. */
export function buildTimeoutMessage(elapsedMs: number): string {
  return `No response after ${Math.round(elapsedMs / 1000)}s — the user may be away from keyboard. Proceed using your best judgment based on the context so far; you can re-ask this question later if it's still relevant.`
}

/**
 * Advertised to the provider verbatim (field order, prose, and constraints all
 * match Claude Code). Execution validates more leniently; this only narrows
 * what the model is told.
 */
export const ASK_USER_QUESTION_INPUT_SCHEMA = {
  type: 'object' as const,
  additionalProperties: false,
  properties: {
    taskId: {
      description:
        'The task this decision belongs to. When set, the question is posted and the turn carries on; the answer queues a later turn.',
      type: 'string',
    },
    recommended: {
      description:
        'The label of the option you recommend. Required for a task-linked question.',
      type: 'string',
    },
    blocking: {
      default: false,
      description:
        'True only when no useful work can continue without the answer. A task-linked question is non-blocking by default.',
      type: 'boolean',
    },
    questions: {
      description: 'Questions to ask the user (1-4 questions)',
      items: {
        additionalProperties: false,
        properties: {
          question: {
            description:
              'The complete question to ask the user. Should be clear, specific, and end with a question mark. Example: "Which library should we use for date formatting?" If multiSelect is true, phrase it accordingly, e.g. "Which features do you want to enable?"',
            type: 'string',
          },
          header: {
            description:
              'Very short label displayed as a chip/tag (max 12 chars). Examples: "Auth method", "Library", "Approach".',
            type: 'string',
          },
          multiSelect: {
            default: false,
            description:
              'Set to true to allow multiple answers to be selected for a question instead of just one. Use when choices are not mutually exclusive.',
            type: 'boolean',
          },
          options: {
            description:
              "The available choices for this question. Must have 2-4 options. Each option should be a distinct, mutually exclusive choice (unless multiSelect is enabled). There should be no 'Other' option, that will be provided automatically.",
            items: {
              additionalProperties: false,
              properties: {
                label: {
                  description:
                    'The display text for this option that the user will see and select. Should be concise (1-5 words) and clearly describe the choice.',
                  type: 'string',
                },
                description: {
                  description:
                    'Explanation of what this option means or what will happen if chosen. Useful for providing context about trade-offs or implications.',
                  type: 'string',
                },
                preview: {
                  description:
                    'Optional preview content rendered when this option is focused. Use for mockups, code snippets, or visual comparisons that help users compare options. See the tool description for the expected content format.',
                  type: 'string',
                },
              },
              required: ['label', 'description'],
              type: 'object',
            },
            maxItems: 4,
            minItems: 2,
            type: 'array',
          },
        },
        required: ['question', 'header', 'options', 'multiSelect'],
        type: 'object',
      },
      maxItems: 4,
      minItems: 1,
      type: 'array',
    },
  },
  required: ['questions'],
}

// ---------------------------------------------------------------------------
// Parse / validate / translate.
// ---------------------------------------------------------------------------

function text(value: unknown): string {
  return typeof value === 'string' ? value : ''
}

/**
 * Read the model's raw tool arguments into `ToolInput`.
 *
 * Tolerant by design: tool args arrive having crossed a JSON boundary (and, on
 * the web path, an Automerge one), so this coerces rather than rejecting on
 * shape. Semantic rules are enforced separately by `validateToolInput`, where
 * the failure can carry Claude's own steering text instead of a parse error.
 */
export function parseToolInput(args: Record<string, unknown>): ToolInput {
  const raw = Array.isArray(args.questions) ? args.questions : []
  const questions: ToolQuestion[] = raw.map((q) => {
    const item = (q ?? {}) as Record<string, unknown>
    const options = Array.isArray(item.options) ? item.options : []
    return {
      question: text(item.question).trim(),
      header: text(item.header).trim(),
      multiSelect: item.multiSelect === true,
      options: options.map((o) => {
        const opt = (o ?? {}) as Record<string, unknown>
        const preview = text(opt.preview).trim()
        return {
          label: text(opt.label).trim(),
          description: text(opt.description).trim(),
          ...(preview.length > 0
            ? { preview: preview.slice(0, MAX_OPTION_PREVIEW_LENGTH) }
            : {}),
        }
      }),
    }
  })
  return { questions }
}

/**
 * The semantic rules Claude enforces outside its JSON schema, in Claude's own
 * order: arity first (it carries the strongest steer), then uniqueness.
 * Returns Claude's verbatim message, or null when the input is acceptable.
 */
export function validateToolInput(input: ToolInput): string | null {
  if (input.questions.length === 0) {
    return 'AskUserQuestion needs at least one question.'
  }
  if (input.questions.length > MAX_QUESTIONS) {
    return `AskUserQuestion accepts at most ${MAX_QUESTIONS} questions in one call.`
  }
  if (
    input.questions.some(
      (q) => q.question.length === 0 || q.header.length === 0,
    )
  ) {
    return 'Every question needs both `question` text and a short `header`.'
  }
  if (input.questions.some((q) => q.options.length < 2)) {
    return TOO_FEW_OPTIONS_MESSAGE
  }
  if (input.questions.some((q) => q.options.length > MAX_OPTIONS)) {
    return `A question accepts at most ${MAX_OPTIONS} options.`
  }
  if (
    input.questions.some((q) =>
      q.options.some((o) => o.label.length === 0 || o.description.length === 0),
    )
  ) {
    return 'Every option needs a non-empty label and description.'
  }
  const prompts = input.questions.map((q) => q.question)
  if (new Set(prompts).size !== prompts.length) return NOT_UNIQUE_MESSAGE
  for (const question of input.questions) {
    const labels = question.options.map((o) => o.label)
    if (new Set(labels).size !== labels.length) return NOT_UNIQUE_MESSAGE
  }
  return null
}

function questionId(index: number): string {
  return `q${index}`
}

function optionValue(index: number, optionIndex: number): string {
  return `${questionId(index)}o${optionIndex}`
}

/** The model's input as the body a surface renders. */
export function buildQuestionBody(input: ToolInput): QuestionBody {
  return {
    questions: input.questions.map((question, index) => ({
      id: questionId(index),
      prompt: question.question,
      shortLabel: question.header,
      multiSelect: question.multiSelect === true,
      options: question.options.map((option, optionIndex) => ({
        value: optionValue(index, optionIndex),
        label: option.label,
        description: option.description,
        // Previews are single-select only, as the description states: with
        // several options selected there is no single artifact to show.
        ...(question.multiSelect !== true && option.preview !== undefined
          ? { preview: option.preview }
          : {}),
      })),
      // Always on, matching Claude: "Users will always be able to select
      // 'Other' to provide custom text input".
      allowFreeText: true,
    })),
  }
}

/** Short label for the prompt a surface shows in a list. */
export function buildInteractionTitle(body: QuestionBody): string {
  const [first] = body.questions
  if (body.questions.length === 1 && first) return first.shortLabel
  return `${body.questions.length} questions`
}

function selectedOptions(
  question: InteractionQuestion,
  answer: InteractionAnswerValue,
): InteractionOption[] {
  return answer.selected.flatMap((value) => {
    const option = question.options.find((c) => c.value === value)
    return option === undefined ? [] : [option]
  })
}

/**
 * One answer line, in the format Claude's native path produces: selected labels
 * joined with ", ", and free text appended after "; " when the user both picked
 * options and typed something.
 */
function buildAnswerText(
  question: InteractionQuestion,
  answer: InteractionAnswerValue,
): string {
  const labels = selectedOptions(question, answer).map((o) => o.label)
  if (labels.length > 0) {
    const selectedText = labels.join(', ')
    return answer.freeText
      ? `${selectedText}; ${answer.freeText}`
      : selectedText
  }
  return answer.freeText ?? ''
}

function buildAnnotation(
  question: InteractionQuestion,
  answer: InteractionAnswerValue,
): ToolResultAnnotation | null {
  // `notes` only when free text accompanies a real selection — free text on its
  // own is already the whole answer, so repeating it would be noise.
  const notes =
    answer.selected.length > 0 && answer.freeText !== undefined
      ? answer.freeText
      : undefined
  const previews = selectedOptions(question, answer).flatMap((o) =>
    o.preview === undefined ? [] : [o.preview],
  )
  const preview = previews.length > 0 ? previews.join('\n\n') : undefined
  if (notes === undefined && preview === undefined) return null
  return {
    ...(preview === undefined ? {} : { preview }),
    ...(notes === undefined ? {} : { notes }),
  }
}

/** The answered body as the result the model reads. */
export function buildToolResult(
  body: QuestionBody,
  response: QuestionAnswer,
): ToolResult {
  const answers: Record<string, string> = {}
  const annotations: Record<string, ToolResultAnnotation> = {}
  /**
   * Claude's `response` field is "freeform text the user typed instead of
   * selecting a structured option" — a single string, so it is only meaningful
   * when one question was asked. `answers` still carries the same text, so
   * nothing is lost for multi-question calls.
   */
  let freeformResponse: string | undefined
  for (const question of body.questions) {
    const answer = response.answers[question.id]
    if (answer === undefined) continue
    const answerText = buildAnswerText(question, answer)
    if (answerText.length === 0) continue
    answers[question.prompt] = answerText
    if (
      body.questions.length === 1 &&
      answer.selected.length === 0 &&
      answer.freeText !== undefined
    ) {
      freeformResponse = answer.freeText
    }
    const annotation = buildAnnotation(question, answer)
    if (annotation !== null) annotations[question.prompt] = annotation
  }
  return {
    questions: body.questions.map((question) => ({
      question: question.prompt,
      header: question.shortLabel,
      options: question.options.map((option) => ({
        label: option.label,
        description: option.description ?? option.label,
        ...(option.preview === undefined ? {} : { preview: option.preview }),
      })),
      multiSelect: question.multiSelect,
    })),
    answers,
    ...(freeformResponse === undefined ? {} : { response: freeformResponse }),
    ...(Object.keys(annotations).length > 0 ? { annotations } : {}),
  }
}

/** Read an answer body back off the log, tolerantly. */
export function parseQuestionAnswer(answerJson: string): QuestionAnswer {
  if (answerJson.trim().length === 0) return { answers: {} }
  let parsed: unknown
  try {
    parsed = JSON.parse(answerJson)
  } catch {
    return { answers: {} }
  }
  const raw = (parsed as { answers?: unknown })?.answers
  if (raw === null || typeof raw !== 'object') return { answers: {} }
  const answers: Record<string, InteractionAnswerValue> = {}
  for (const [key, value] of Object.entries(raw as Record<string, unknown>)) {
    const item = (value ?? {}) as Record<string, unknown>
    const selected = Array.isArray(item.selected)
      ? item.selected.map((s) => String(s))
      : []
    const freeText = text(item.freeText).trim()
    answers[key] = {
      selected,
      ...(freeText.length > 0
        ? { freeText: freeText.slice(0, MAX_FREE_TEXT_LENGTH) }
        : {}),
    }
  }
  return { answers }
}

/** Read a question body back off the log, tolerantly. */
export function parseQuestionBody(bodyJson: string): QuestionBody {
  let parsed: unknown
  try {
    parsed = JSON.parse(bodyJson)
  } catch {
    return { questions: [] }
  }
  const raw = (parsed as { questions?: unknown })?.questions
  if (!Array.isArray(raw)) return { questions: [] }
  return {
    questions: raw.map((q, index) => {
      const item = (q ?? {}) as Record<string, unknown>
      const options = Array.isArray(item.options) ? item.options : []
      return {
        id: text(item.id) || questionId(index),
        prompt: text(item.prompt),
        shortLabel: text(item.shortLabel),
        multiSelect: item.multiSelect === true,
        allowFreeText: item.allowFreeText !== false,
        options: options.map((o, optionIndex) => {
          const opt = (o ?? {}) as Record<string, unknown>
          const description = text(opt.description)
          const preview = text(opt.preview)
          return {
            value: text(opt.value) || optionValue(index, optionIndex),
            label: text(opt.label),
            ...(description.length > 0 ? { description } : {}),
            ...(preview.length > 0 ? { preview } : {}),
          }
        }),
      }
    }),
  }
}
